/**
 * Announcer barks (DESIGN-client §8): at most one bark per 30s unless `priority:'major'`,
 * subtitle pushed to `presentation.subtitle`, the clip played on the announcer bus.
 *
 * Clip selection lives in `announcerClips.ts`. A pack keys its lines by rendered text, so a bark's
 * `subtitle` is what finds its audio; the cue also carries the `{team}`/`{n}` fills, which let a
 * line with no clip of its own fall back to another variant that says the same thing about the
 * hand. A fallback speaks different words than the server rendered, so `bark` shows the clip's own
 * line as the subtitle: what is on screen is always what is being said. With no pack installed —
 * the default until someone runs the generator — the lookup returns null and the bark is
 * subtitle-only.
 */
import type { AnyCue, CueData } from '../scene/cues/cueTypes';
import { announcerClips } from './announcerClips';
import type { AudioEngine } from './AudioEngine';
import type { CueStartSource } from './SfxDirector';

export type AnnouncerBark = CueData['announcerBark'];
export type Subtitle = { text: string; until: number } | null;
export type ClipVars = { team?: string | number; n?: string | number };
/**
 * `subtitle` is the rendered line the cue carried and `index` its seq; either can identify the
 * variant the server chose. `vars` are the cue's `{team}`/`{n}` fills, which let a bark whose exact
 * line has no clip fall back to another variant that means the same thing.
 *
 * Returns the sample id, the line that clip actually speaks, and whether its audio is decoded and
 * ready. The text matters: on a fallback the clip says a different sentence than the cue's subtitle,
 * and the caller shows that one instead so the words on screen are the words being spoken. `loaded`
 * is what keeps that honest — swapping the words in while the clip is still downloading would show a
 * line nothing is saying.
 */
export type ClipHit = { id: string; text: string; loaded: boolean };
export type ClipLookup = (
  eventKey: string,
  voiceId: string,
  subtitle?: string,
  index?: number,
  vars?: ClipVars,
) => ClipHit | null;

export const ANNOUNCER_CAP_MS = 30_000;
export const SUBTITLE_MS = 2800;

/** Prefix every generated clip id starts with (`${eventKey}:${voiceId}#${variant}|…`). */
export const clipId = (eventKey: string, voiceId: string): string => `${eventKey}:${voiceId}`;

/**
 * Bare-id registration, kept for callers that only know ids. A pack registered this way can be
 * looked up but carries no url, so `announcerClips` cannot load its audio; the full path is
 * `registerAnnouncerClips` in `announcerClips.ts`.
 */
export function registerAnnouncerClipIds(ids: Iterable<string>): void {
  const clips = [...ids].map((id) => ({ id, url: '', eventKey: id.split(':')[0] ?? id, variant: 0, vars: {}, text: '' }));
  announcerClips.add(clips);
}

export const lookupClip: ClipLookup = (eventKey, voiceId, subtitle, index, vars) => {
  const hit = announcerClips.find(eventKey, voiceId, { text: subtitle, index, vars });
  return hit ? { id: hit.id, text: hit.text, loaded: announcerClips.isLoaded(hit.id) } : null;
};

export interface AnnouncerOptions {
  setSubtitle: (s: Subtitle) => void;
  subtitlesEnabled?: () => boolean;
  now?: () => number;
  capMs?: number;
  subtitleMs?: number;
  clips?: ClipLookup;
}

export class Announcer {
  readonly capMs: number;
  readonly subtitleMs: number;
  lastAt = Number.NEGATIVE_INFINITY;
  private readonly now: () => number;
  private readonly clips: ClipLookup;
  private unsub: (() => void) | null = null;

  constructor(
    private readonly engine: Pick<AudioEngine, 'play'>,
    private readonly opts: AnnouncerOptions,
  ) {
    this.capMs = opts.capMs ?? ANNOUNCER_CAP_MS;
    this.subtitleMs = opts.subtitleMs ?? SUBTITLE_MS;
    this.now = opts.now ?? Date.now;
    this.clips = opts.clips ?? lookupClip;
  }

  attach(source: CueStartSource): () => void {
    this.detach();
    this.unsub = source.onStart((cue) => {
      const c = cue as AnyCue;
      if (c.type === 'announcerBark') this.bark(c.data, cue.seq);
    });
    return () => this.detach();
  }

  detach(): void {
    this.unsub?.();
    this.unsub = null;
  }

  /** Returns whether the bark was delivered (subtitle and/or clip) or dropped by the cap. */
  bark(b: AnnouncerBark, index?: number): { spoke: boolean; clipId: string | null } {
    const t = this.now();
    if (b.priority !== 'major' && t - this.lastAt < this.capMs) return { spoke: false, clipId: null };
    this.lastAt = t;
    const hit = this.clips(b.eventKey, b.voiceId, b.subtitle, index, b.vars);
    if (hit) this.engine.play(hit.id, { bus: 'announcer' });
    // A fallback clip speaks a different sentence than the server rendered, so the subtitle follows
    // the audio rather than the cue: the two must never contradict each other on screen. Only once the
    // clip can actually be heard, though — `play` on a clip that has not decoded is a silent no-op
    // (`AudioEngine.play`), and swapping the words in anyway would caption audio that never came.
    const text = (hit?.loaded ? hit.text : '') || b.subtitle;
    if (text && (this.opts.subtitlesEnabled?.() ?? true)) {
      this.opts.setSubtitle({ text, until: t + this.subtitleMs });
    }
    return { spoke: true, clipId: hit?.id ?? null };
  }
}
